import { Building2, Calendar, FolderOpen, Layers, Tag } from "lucide-react";
import type { DonationCampaign } from "../../donation/types";

function formatCampaignDateRange(start?: string, end?: string): string | null {
  if (!start && !end) {
    return null;
  }
  const fmt = (d: string) => {
    const parsed = new Date(d);
    if (Number.isNaN(parsed.getTime())) {
      return d;
    }
    return parsed.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };
  if (start && end) {
    return `${fmt(start)} – ${fmt(end)}`;
  }
  return fmt(start ?? end!);
}

interface DonationCampaignInfoCardProps {
  campaign: DonationCampaign;
}

export default function DonationCampaignInfoCard({
  campaign,
}: DonationCampaignInfoCardProps) {
  const dateRange = formatCampaignDateRange(
    campaign.startDate,
    campaign.endDate
  );

  const metaItems = [
    {
      label: "Institution",
      value: campaign.institutionName,
      icon: Building2,
    },
    {
      label: "Donation type",
      value: campaign.typeOfDonationName,
      icon: Tag,
    },
    {
      label: "Category",
      value: campaign.categoryName,
      icon: FolderOpen,
    },
    {
      label: "Sub category",
      value: campaign.subCategory,
      icon: Layers,
    },
    {
      label: "Campaign period",
      value: dateRange,
      icon: Calendar,
    },
  ].filter(item => item.value);

  if (!campaign.description && metaItems.length === 0) {
    return null;
  }

  return (
    <div className="rounded-2xl border border-gray-100/80 bg-white p-6 shadow-[0_4px_24px_rgba(122,30,30,0.06)]">
      <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.12em] text-gray-400">
        About this campaign
      </p>

      {campaign.description && (
        <p className="text-sm leading-relaxed text-gray-700">
          {campaign.description}
        </p>
      )}

      {metaItems.length > 0 && (
        <div
          className={`grid gap-2.5 ${campaign.description ? "mt-4 border-t border-gray-100 pt-4" : ""}`}
        >
          {metaItems.map(({ label, value, icon: Icon }) => (
            <div
              key={label}
              className="flex gap-3 rounded-xl bg-[#FFF9F0] px-3 py-2.5"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#7A1E1E]/10 text-[#7A1E1E]">
                <Icon className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                  {label}
                </p>
                <p className="mt-0.5 text-sm font-medium text-[#7A1E1E]">
                  {value}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
